import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { autorun } from 'mobx';
import VideoDialog from '../components/dialog/VideoDialog';
import { ExtendedVideo, Video, VideoWithImage } from '../types/transport';
import AddVideoDialog from '../components/dialog/AddVideoDialog';
import { useStore } from '../store/context';
import {
  addVideoThumbnail,
  deleteVideoImage,
  getThumbnailImage,
} from '../firebaseConnection/storage';

const VideoDetailPage = observer((): JSX.Element => {
  const [showVideo, setPlayVideo] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<Video>();
  const { videoStore } = useStore();
  const [categories, setCategories] = useState<ExtendedVideo[]>([]);

  useEffect(
    () =>
      autorun(() => {
        (async function getCategories() {
          if (videoStore?.allEntries) {
            const allEntries = await Promise.all(
              videoStore?.allEntries.map(async entry => {
                try {
                  const imageFileName = await getThumbnailImage(
                    entry.thumbnailUrl,
                  );
                  return { ...entry, localThumbnailImageUrl: imageFileName };
                } catch (e) {
                  return { ...entry, localThumbnailImageUrl: '' };
                }
              }),
            );

            setCategories(allEntries);
          }
        })();
      }),
    [],
  );

  const playVideo = () => {
    setPlayVideo(true);
  };
  const { id } = useParams();

  const editVideo = (video: Video) => {
    setCurrentVideo(video);
    setShowAddVideo(true);
  };

  const addVideo = async (video: VideoWithImage, image: File) => {
    if (image.size > 0) {
      try {
        await addVideoThumbnail(video.thumbnailUrl, image);
      } catch (e) {
        console.log(e);
      }
    }
    const uploadEntry = video;
    delete uploadEntry.localThumbnailImageUrl;
    videoStore?.updateVideoCategory(video.uuid, uploadEntry);

    setShowAddVideo(false);
  };

  const deleteVideoEntry = async (video: VideoWithImage) => {
    try {
      if (video.thumbnailUrl) await deleteVideoImage(video.thumbnailUrl);
      await videoStore?.updateVideoCategory(video.uuid, null);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <Container sx={{ marginLeft: '240px', width: '100%' }}>
        <Box
          component="main"
          sx={{ flexGrow: 1, bgcolor: 'background.default', p: 2 }}
        >
          <Button
            variant="contained"
            onClick={() => {
              setCurrentVideo(undefined);
              setShowAddVideo(true);
            }}
            endIcon={<AddIcon />}
            sx={{ mt: 2, mb: 2 }}
          >
            Neues Video hinzufügen
          </Button>
          {categories
            .filter(video => video.category === id)
            .map(video => (
              <Grid container key={video.uuid} sx={{ mb: 4, minHeight: 200 }}>
                <Grid item xs={4} style={{ cursor: 'pointer' }}>
                  <img
                    onClick={playVideo}
                    role="presentation"
                    alt={video.thumbnailUrl}
                    style={{ maxWidth: 300 }}
                    src={
                      video.localThumbnailImageUrl ||
                      'https://via.placeholder.com/150.png/09f/fff%20C/O%20https://placeholder.com/'
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h5">{video.title}</Typography>

                  <Typography
                    sx={{ wordBreak: 'break-word' }}
                    variant="subtitle2"
                  >
                    {video.description}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  style={{ display: 'flex', alignItems: 'flex-end' }}
                >
                  <Button
                    onClick={() => editVideo(video)}
                    endIcon={<EditIcon />}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => deleteVideoEntry(video)}
                    endIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                </Grid>
              </Grid>
            ))}

          {showVideo && (
            <VideoDialog
              source={currentVideo?.url || ''}
              onClose={() => setPlayVideo(false)}
            />
          )}

          {showAddVideo && (
            <AddVideoDialog
              category={id || ''}
              video={currentVideo}
              onSave={(video: VideoWithImage, image: File) =>
                addVideo(video, image)
              }
              onClose={() => setShowAddVideo(false)}
            />
          )}
        </Box>
      </Container>
    </>
  );
});

export default VideoDetailPage;
